// FILE: src/components/FeedbackButton.js
//
// Global floating action button for the Feedback Hub. Mounted as a sibling of
// <App /> in index.js (next to HeaderUserMenu) so it is always present
// regardless of which view App renders — the same pattern HeaderUserMenu uses.
//
// Visibility: shown to signed-in learners on every screen (per spec). Anonymous
// visitors don't see it; they're prompted to sign in elsewhere.
//
// Context auto-fill: App publishes the current { route, pageName, certId,
// certLabel } via the `pm-pixel:context` CustomEvent and a window global. We
// read the global on open and keep it fresh via the event, with a location-based
// fallback so the button still works if App hasn't published yet.

import React, { useEffect, useState, useCallback } from 'react';
import { useLearnerAuth } from '../../contexts/LearnerAuthContext';
import { createFeedback } from '../../services/feedbackService';
import { trackEvent } from '../../services/telemetryService';
import FeedbackModal from './FeedbackModal';

// Fallback route → page-name map when App hasn't published context yet.
const VIEW_LABELS = {
  home: 'Home', dashboard: 'Dashboard', readiness: 'Readiness',
  plan: 'Study Plan', milestones: 'Milestones', history: 'Performance History',
};

function readContext() {
  if (typeof window === 'undefined') return { pageName: 'Home', route: 'home' };
  if (window.__pmPixelFeedbackContext) return window.__pmPixelFeedbackContext;
  try {
    const v = new URL(window.location.href).searchParams.get('view') || 'home';
    return { route: v, pageName: VIEW_LABELS[v] || 'Home', certId: null, certLabel: null };
  } catch (_) {
    return { route: 'home', pageName: 'Home', certId: null, certLabel: null };
  }
}

const styles = {
  fab: {
    position: 'fixed', right: '24px', bottom: '24px', zIndex: 1500,
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: '12px 18px', borderRadius: '999px', border: 'none',
    background: '#2c63ff', color: '#fff', fontSize: '0.95rem', fontWeight: 700,
    fontFamily: "'Segoe UI', system-ui, sans-serif", cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(44,99,255,0.4)',
  },
  fabIcon: { fontSize: '1.15rem', lineHeight: 1 },
};

export default function FeedbackButton() {
  const { user, loading } = useLearnerAuth();
  const [open, setOpen]   = useState(false);
  const [ctx, setCtx]     = useState(readContext);

  // Admin screens pin the Quick Actions ribbon (~52px) to the bottom edge;
  // lift the FAB above it there so the two never overlap.
  const onAdmin = String(ctx && ctx.route ? ctx.route : '').startsWith('admin');

  // Stay in sync with App's published context.
  useEffect(() => {
    function onCtx(e) {
      if (e?.detail) setCtx(e.detail);
    }
    window.addEventListener('pm-pixel:context', onCtx);
    return () => window.removeEventListener('pm-pixel:context', onCtx);
  }, []);

  const handleOpen = useCallback(() => {
    setCtx(readContext());
    setOpen(true);
    try { trackEvent('feedback_opened', 'usage', { page: readContext().pageName }); } catch (_) { /* never blocks */ }
  }, []);

  const handleSubmit = useCallback((payload) => {
    // Fire-and-forget; the modal handles its own confirmation UI.
    createFeedback(payload);
    try {
      trackEvent('feedback_submitted', 'usage', {
        category: payload.category, severity: payload.severity, page: payload.pageName,
      });
    } catch (_) { /* never blocks */ }
  }, []);

  // Anonymous visitors and the initial auth probe: render nothing.
  if (loading || !user) return null;

  return (
    <>
      <button
        type="button"
        data-testid="feedback-fab"
        style={{ ...styles.fab, bottom: onAdmin ? '84px' : '24px' }}
        onClick={handleOpen}
        aria-label="Share feedback"
        title="Share feedback"
      >
        <span style={styles.fabIcon} aria-hidden="true">💬</span>
        Feedback
      </button>
      {open && (
        <FeedbackModal
          defaults={ctx}
          onSubmit={handleSubmit}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
